import type { Insurance } from "@/types/insurance/insurance";
import { toFixedNumber } from "../utils";

const DEFAULT_REDUCTION_RATE_PCT = 5;
const MIN_COMPENSATION_PERCENT = 0.2;
const DEFAULT_TAPER_START_AGE = 46;

const getTaperStartAge = (insurance: Insurance | undefined): number => {
	const raw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-allowance-taper-start-age"
		)?.value as string
	);
	return !Number.isNaN(raw) && raw >= 0 ? raw : DEFAULT_TAPER_START_AGE;
};

const getTaperUnit = (insurance: Insurance | undefined): "percent" | "g" => {
	const raw = insurance?.params?.find(
		(param) => param.type === "dependents-allowance-taper-unit"
	)?.value as string;
	return raw === "g" ? "g" : "percent";
};

/** Raw value of segment-1 rate (percent or G depending on unit). Falls back to 5 (5%). */
const getTaper1Rate = (insurance: Insurance | undefined): number => {
	const raw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-supplement-age-reduction-rate"
		)?.value as string
	);
	return !Number.isNaN(raw) && raw > 0 ? raw : DEFAULT_REDUCTION_RATE_PCT;
};

/**
 * Compute reduction for one taper segment.
 * - unit "percent": base × (rateRaw / 100) × years
 * - unit "g":       rateRaw × years  (absolute G deduction)
 */
const computeSegmentReduction = (
	base: number,
	rateRaw: number,
	years: number,
	unit: "percent" | "g",
): number => {
	if (unit === "g") return rateRaw * years;
	return base * (rateRaw / 100) * years;
};

/**
 * Apply up to two taper segments to basePayout, respecting the configured
 * start age, unit (% or G), optional segment-1 end age, and optional segment 2.
 * Returns the floored payout (minimum = basePayout × MIN_COMPENSATION_PERCENT).
 *
 * Backward-compatible: when the new params are absent the behaviour is
 * identical to the old single-segment 5%-per-year from age 46.
 */
const applyTapering = (
	basePayout: number,
	insuredAge: number,
	insurance: Insurance | undefined,
): number => {
	const taperStartAge = getTaperStartAge(insurance);
	if (insuredAge <= taperStartAge) return basePayout;

	const unit = getTaperUnit(insurance);
	const rate1 = getTaper1Rate(insurance);

	const taper1EndAgeRaw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-allowance-taper-1-end-age"
		)?.value as string
	);
	const hasTaper1End =
		!Number.isNaN(taper1EndAgeRaw) && taper1EndAgeRaw > taperStartAge;

	const taper2RateRaw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-allowance-taper-2-rate"
		)?.value as string
	);
	const hasTaper2Rate = !Number.isNaN(taper2RateRaw) && taper2RateRaw > 0;

	const taper2EndAgeRaw = parseFloat(
		insurance?.params?.find(
			(param) => param.type === "dependents-allowance-taper-2-end-age"
		)?.value as string
	);
	const hasTaper2End =
		!Number.isNaN(taper2EndAgeRaw) &&
		taper2EndAgeRaw > (hasTaper1End ? taper1EndAgeRaw : taperStartAge);

	// Segment 1: from taperStartAge up to taper1EndAge (or the insured's current age)
	const seg1Cap = hasTaper1End ? taper1EndAgeRaw : insuredAge;
	const years1 = Math.min(insuredAge, seg1Cap) - taperStartAge;
	let reduction = computeSegmentReduction(basePayout, rate1, years1, unit);

	// Segment 2: only applies when segment-1 has an end age and segment-2 has a rate.
	// taper-2-start-age allows a gap between the segments; defaults to taper1EndAge.
	// taper-2-unit can differ from segment 1's unit; falls back to the shared unit.
	if (hasTaper1End && hasTaper2Rate) {
		const taper2StartAgeRaw = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "dependents-allowance-taper-2-start-age"
			)?.value as string
		);
		const seg2Start = !Number.isNaN(taper2StartAgeRaw) && taper2StartAgeRaw >= taper1EndAgeRaw
			? taper2StartAgeRaw
			: taper1EndAgeRaw;

		const taper2UnitRaw = insurance?.params?.find(
			(param) => param.type === "dependents-allowance-taper-2-unit"
		)?.value as string | undefined;
		const unit2: "percent" | "g" = taper2UnitRaw === "g" ? "g" : taper2UnitRaw === "percent" ? "percent" : unit;

		if (insuredAge > seg2Start) {
			const seg2Cap = hasTaper2End ? taper2EndAgeRaw : insuredAge;
			const years2 = Math.min(insuredAge, seg2Cap) - seg2Start;
			reduction += computeSegmentReduction(
				basePayout,
				taper2RateRaw,
				Math.max(0, years2),
				unit2,
			);
		}
	}

	const reduced = basePayout - reduction;
	const minPayout = basePayout * MIN_COMPENSATION_PERCENT;
	return reduced < minPayout ? minPayout : reduced;
};

export const dependentsAllowanceBaseCoverage = (
	insurance: Insurance | undefined,
	childAges: number[],
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null,
	insuredAge?: number,
) => {
	let baseCoverage: number;
	let childMaxAge: number;
	let minAge: number;
	let maxAge: number;

	try {
		baseCoverage = parseFloat(
			insurance?.params?.find((param) => param.type === "base-compensation")
				?.value as string
		);
		childMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child"
			)?.value as string
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string
		);
	} catch (error) {
		console.error("Error in dependentsAllowanceBaseCoverage:", error);
		return 0;
	}

	if (Number.isNaN(minAge) || minAge < 0) minAge = 0;
	if (Number.isNaN(maxAge) || maxAge <= 0) maxAge = 72;

	if (insuredAge !== undefined && (insuredAge < minAge || insuredAge >= maxAge)) {
		return 0;
	}

	if (maritalStatus === "single" && childAges.length === 0) {
		return 0;
	} else if (maritalStatus === "single") {
		let hasChildrenInAgeRange = false;
		for (const age of childAges) {
			if (age >= 0 && age <= childMaxAge) {
				hasChildrenInAgeRange = true;
				break;
			}
		}
		if (!hasChildrenInAgeRange) {
			return 0;
		}
	}

	if (!baseCoverage || baseCoverage < 0) {
		console.error("Base coverage is out of range");
		return 0;
	}

	const fixedAllowance = insurance?.params?.find(
		(param) => param.type === "fixed-dependents-allowance"
	)?.value as boolean | undefined;

	let payout = baseCoverage;

	if (!fixedAllowance && insuredAge !== undefined) {
		payout = applyTapering(baseCoverage, insuredAge, insurance);
	}

	return toFixedNumber(payout);
};

export const dependentsAllowanceAnnualSalary = (
	insurance: Insurance | undefined,
	annualSalary: number,
	childAges: number[],
	maritalStatus: "single" | "married" | "cohabitant" | "partner" | null,
	insuredAge?: number,
) => {
	let salaryMultiplier: number;
	let minPayout: number;
	let maxPayout: number;
	let childMaxAge: number;
	let minAge: number;
	let maxAge: number;

	try {
		salaryMultiplier = parseFloat(
			insurance?.params?.find((param) => param.type === "annual-salary")
				?.value as string
		);
		minPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "min-compensation")
				?.value as string
		);
		maxPayout = parseFloat(
			insurance?.params?.find((param) => param.type === "max-compensation")
				?.value as string
		);
		childMaxAge = parseFloat(
			insurance?.params?.find(
				(param) => param.type === "max-age-child"
			)?.value as string
		);
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string
		);
	} catch (error) {
		console.error("Error in dependentsAllowanceBaseCoverage:", error);
		return 0;
	}

	if (Number.isNaN(minAge) || minAge < 0) minAge = 0;
	if (Number.isNaN(maxAge) || maxAge <= 0) maxAge = 72;

	if (insuredAge !== undefined && (insuredAge < minAge || insuredAge >= maxAge)) {
		return 0;
	}

	if (maritalStatus === "single" && childAges.length === 0) {
		return 0;
	} else if (maritalStatus === "single") {
		let hasChildrenInAgeRange = false;
		for (const age of childAges) {
			if (age >= 0 && age <= childMaxAge) {
				hasChildrenInAgeRange = true;
				break;
			}
		}
		if (!hasChildrenInAgeRange) {
			return 0;
		}
	}

	if (!salaryMultiplier || salaryMultiplier < 0) {
		console.error("Salary multiplier is out of range");
		return 0;
	}

	if (!annualSalary || annualSalary < 0) {
		console.error("Annual salary is out of range");
		return 0;
	}

	const rawPayout = annualSalary * salaryMultiplier;

	// * If minPayout or maxPayout are not set,
	// * this behaves as not having a boundary,
	// * which is the desired effect
	let result = rawPayout;
	if (rawPayout < minPayout) {
		result = minPayout;
	} else if (rawPayout > maxPayout) {
		result = maxPayout;
	}

	const basePayout = result;
	const fixedAllowance = insurance?.params?.find(
		(param) => param.type === "fixed-dependents-allowance"
	)?.value as boolean | undefined;

	if (!fixedAllowance && insuredAge !== undefined) {
		result = applyTapering(basePayout, insuredAge, insurance);
	}

	return toFixedNumber(result);
};
